import { Star } from "lucide-react";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { useLocale } from "@/i18n/LocaleContext";
import type { PublicProfile } from "@/lib/users-api";
import { publicHostReviewHeadline, publicReviewDate, publicReviewerName } from "@/lib/public-host-reviews.mjs";

export function PublicHostReviewHeadline({ profile }: { profile: PublicProfile }) {
  const { t } = useAppTranslation();
  const stats = publicHostReviewHeadline(profile);
  if (!stats.reviewCount || stats.averageRating == null) return null;
  return <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-host-review-headline>
    <Star className="h-4 w-4 fill-brand text-brand dark:fill-foreground dark:text-foreground" aria-hidden="true" />
    <span className="font-semibold text-foreground">{stats.averageRating.toFixed(1)}</span>
    <span>{t("collection_reviews.count", { count: stats.reviewCount, defaultValue: "{{count}} reviews" })}</span>
  </p>;
}

/** Single-rating legacy reviews do not depend on optional category summaries. */
export function PublicHostReviews({ profile }: { profile: PublicProfile }) {
  const { t } = useAppTranslation();
  const { language } = useLocale();
  const reviews = profile.reviews || [];
  return <section className="border-t border-border py-8" aria-labelledby="public-host-reviews">
    <h2 id="public-host-reviews" className="mb-5 text-2xl font-semibold text-brand dark:text-foreground">
      {t("collection_reviews.heading", "Reviews")}
    </h2>
    {reviews.length ? <div className="space-y-3">
      {reviews.map(review => <article key={review.id} data-public-host-review={review.id}
        className="rounded-2xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center gap-2">
          {review.rating != null && Number(review.rating) >= 1 && Number(review.rating) <= 5 && <span
            className="flex gap-0.5" role="img"
            aria-label={t("collection_reviews.stars", { rating: review.rating, defaultValue: "{{rating}} out of 5 stars" })}>
            {[1, 2, 3, 4, 5].map(star => <Star key={star} aria-hidden="true"
              className={`h-3.5 w-3.5 ${star <= Number(review.rating) ? "fill-brand text-brand dark:fill-foreground dark:text-foreground" : "text-muted-foreground/30"}`} />)}
          </span>}
          <time className="text-xs text-muted-foreground" dateTime={review.createdAt}>
            {publicReviewDate(review.createdAt, language || "en")}
          </time>
        </div>
        {review.reviewText && <p className="mt-2 whitespace-pre-line break-words text-sm text-foreground">{review.reviewText}</p>}
        <p className="mt-2 text-xs text-muted-foreground">{t("collection_reviews.by", {
          name: publicReviewerName(review.reviewer, t("collection_reviews.guest", "Guest")),
          defaultValue: "By {{name}}",
        })}</p>
      </article>)}
    </div> : <div className="rounded-2xl border border-border bg-card p-8 text-center">
      <Star className="mx-auto h-10 w-10 text-muted-foreground/40" aria-hidden="true" />
      <p className="mt-2 text-sm text-muted-foreground">{t("collection_reviews.empty", "No reviews yet")}</p>
    </div>}
  </section>;
}
